import { Alert, message } from 'antd'
import { useHistory, type match } from 'react-router-dom'
import { Box } from '@weareberyl/design-system'

import { useSchemeAddon } from '../Detail/data'
import AddonForm, { AddonFormProps } from '../AddonForm'
import { AddonPageHeader } from '../components'
import { prependScheme } from '~/constants/routes'
import {
  SchemeAddonDocument,
  useUpdateSchemeAddonMutation,
} from '~/gql/generated/graphql'

type Props = {
  match: match<{ scheme_id: string; id: string }>
}

const AddonEdit = ({ match }: Props) => {
  const schemeAddonId = match.params.id
  const { loading, data, initialValues } = useSchemeAddon({ schemeAddonId })
  const history = useHistory()
  const [updateSchemeAddon, { loading: saving, error }] =
    useUpdateSchemeAddonMutation()
  // Display messages from the server if there are errors that get past client side form validation
  const messages = error?.graphQLErrors.map(error => error.message)

  const onFinish: AddonFormProps['onFinish'] = async schemeAddonInput => {
    if (!data?.scheme_addon) {
      return
    }

    try {
      await updateSchemeAddon({
        variables: {
          schemeAddonId,
          schemeAddonInput,
        },
        refetchQueries: [SchemeAddonDocument],
      })
    } catch {
      message.error('There was an error saving the addon')
      return
    }

    history.replace(
      prependScheme(`/addons/${schemeAddonId}`, data.scheme_addon.scheme_id),
    )
    message.success('Addon updated')
  }

  if (loading) {
    return null
  }

  return (
    <>
      <AddonPageHeader
        id={schemeAddonId}
        schemeId={match.params.scheme_id}
        goBack
        isEditing
      />
      {messages && (
        <Box pb={3}>
          <Alert
            message="Couldn't save the addon"
            description={messages.map(message => (
              <p key={message}>{message}</p>
            ))}
            type="error"
            showIcon
          />
        </Box>
      )}
      <AddonForm
        initialValues={initialValues}
        loading={saving || loading}
        onFinish={onFinish}
      />
    </>
  )
}

export default AddonEdit
